/**
 * Which drawing surface the tool wheel talks to.
 *
 * The app mounts ONE wheel (`ToolWheelHost`). Every surface that owns a
 * drawing bar registers its root element here with the handlers that bar
 * runs on: a standalone `EdgeChart` registers its own `handleSelectTool`, a
 * grid registers its broadcast (which arms every cell, so a drawing started in
 * one cell can finish in any). On Tab the host resolves the surface under the
 * pointer, innermost first, and locks it for the whole gesture.
 *
 * Surfaces that render no drawing bar never register, so research mini
 * charts, news charts and Learn players can never arm a drawing.
 */
import { useEffect, useRef, type RefObject } from 'react'
import { hubAdd } from '../core/globalListenerHub'
import type { WheelPoint } from '../utils/toolWheelGeometry'
import type { WheelControls } from './toolWheelControls'

/** The key held to open the wheel, matched on `KeyboardEvent.code`. */
export const TOOL_WHEEL_KEY = 'Tab'

/**
 * A surface's wheel handlers. The mode controls (magnet, lock, hide, remove
 * all) are the same handlers and state the surface hands its DrawingBar; a
 * control slot whose handler is missing shows dimmed and does nothing.
 */
export interface ToolWheelTarget extends WheelControls {
  /** Arms a tool through the surface's own entry point, never `createOverlay` directly. */
  selectTool: (overlayName: string) => void
  /**
   * Turns off the armed tool (and any drawing still being placed), the same
   * as Escape mid-draw. Called when Tab is tapped, or released in the hub.
   */
  cancelTool?: () => void
  /** The armed tool, shown as a dot on its tile. */
  activeTool?: string | null
}

type TargetGetter = () => ToolWheelTarget | null

const targets = new Map<Element, TargetGetter>()
let pointer: WheelPoint | null = null
let wheelOpen = false
let stopTracking: (() => void) | null = null

/** Last pointer position, tracked (one shared listener) while any surface is registered. */
function startTracking(): void {
  const offMove = hubAdd<PointerEvent>('window', 'pointermove', (e) => {
    pointer = { x: e.clientX, y: e.clientY }
  })
  // Leaving the page: a Tab pressed with the mouse elsewhere is not "over" a
  // chart. `mouseleave` on the root fires only for that; a `mouseout` with no
  // related target also fires when the element under the pointer is removed
  // (the wheel itself closing), which must not forget the pointer.
  const root = document.documentElement
  const onLeave = () => {
    pointer = null
  }
  root.addEventListener('mouseleave', onLeave)
  stopTracking = () => {
    offMove()
    root.removeEventListener('mouseleave', onLeave)
  }
}

/** Register `el` as a wheel surface. `get` is read at Tab time, so it can return live handlers. */
export function registerToolWheelTarget(el: Element, get: TargetGetter): () => void {
  targets.set(el, get)
  if (!stopTracking) startTracking()
  let active = true
  return () => {
    if (!active) return
    active = false
    if (targets.get(el) === get) targets.delete(el)
    if (targets.size === 0 && stopTracking) {
      stopTracking()
      stopTracking = null
      pointer = null
    }
  }
}

export function getToolWheelPointer(): WheelPoint | null {
  return pointer
}

/**
 * The innermost registered surface under `point`. Resolved from the element
 * actually painted there, so a dialog, sheet or popover covering the chart
 * blocks the wheel.
 */
export function resolveToolWheelTarget(point: WheelPoint | null = pointer): ToolWheelTarget | null {
  if (!point || targets.size === 0) return null
  if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function')
    return null
  for (let node = document.elementFromPoint(point.x, point.y); node; node = node.parentElement) {
    const target = targets.get(node)?.()
    if (target) return target
  }
  return null
}

/** Set by the host while the wheel is on screen. */
export function setToolWheelOpen(open: boolean): void {
  wheelOpen = open
}

/**
 * Whether a Tab press right now belongs to the wheel: it is open, or a
 * drawing surface is under the pointer. Other page-wide Tab bindings (the
 * Backtester's node command bar) check this and yield.
 */
export function toolWheelOwnsTab(): boolean {
  return wheelOpen || resolveToolWheelTarget() !== null
}

/**
 * Register a surface's root for the wheel. Pass `null` while the surface has
 * no drawing bar (a grid whose bar is hidden during a drill), which
 * unregisters it. The latest `target` is read at Tab time.
 */
export function useToolWheelTarget(
  ref: RefObject<HTMLElement | null>,
  target: ToolWheelTarget | null,
): void {
  const latest = useRef(target)
  latest.current = target
  const enabled = target !== null
  useEffect(() => {
    const el = ref.current
    if (!enabled || !el) return
    return registerToolWheelTarget(el, () => latest.current)
  }, [ref, enabled])
}
